import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { isAuthenticated } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle2, XCircle, Mail } from 'lucide-react';

/**
 * `/verify-email?token=<jwt>`. Landing page for the click in the
 * verification email. Calls `POST /api/auth/verify-email/<token>` once
 * on mount and renders one of three states:
 *
 *   - verifying: spinner (typical 200-500ms)
 *   - success: checkmark + "go to dashboard" CTA
 *   - error: explanation + "resend" link (only available if logged in)
 *
 * Token validity is JWT-based on the server (24h purpose-tagged claim).
 * Re-clicking after success is a no-op. Server returns
 * `{already_verified: true}` which we treat as success.
 */
export default function VerifyEmailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState(token ? 'verifying' : 'error');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage(t('verifyEmail.missingToken') || 'Kein Token in der URL gefunden.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await authAPI.verifyEmail(token);
        if (!cancelled) setStatus('success');
      } catch (err) {
        if (cancelled) return;
        setStatus('error');
        setErrorMessage(
          err.response?.data?.detail ||
            t('verifyEmail.failed') ||
            'Verifizierung fehlgeschlagen. Der Link ist abgelaufen oder ungültig.'
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f7f3ea] px-4">
      <Card className="glass border-[#e3dccb] max-w-md w-full">
        <CardHeader className="pb-3 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-[#dfe8db] flex items-center justify-center mb-3">
            {status === 'verifying' && <Loader2 className="w-6 h-6 text-[#1f4d3a] animate-spin" />}
            {status === 'success' && <CheckCircle2 className="w-6 h-6 text-[#1e7a4f]" />}
            {status === 'error' && <XCircle className="w-6 h-6 text-[#a34430]" />}
          </div>
          <CardTitle className="text-xl text-[#23211a]">
            {status === 'verifying' && (t('verifyEmail.verifying') || 'Wird bestätigt…')}
            {status === 'success' && (t('verifyEmail.success') || 'E-Mail bestätigt!')}
            {status === 'error' && (t('verifyEmail.errorTitle') || 'Verifizierung fehlgeschlagen')}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {status === 'verifying' && (
            <p className="text-sm text-[#6f6a5c]">
              {t('verifyEmail.verifyingHelp') ||
                'Wir prüfen deinen Bestätigungslink. Gleich geht es weiter.'}
            </p>
          )}
          {status === 'success' && (
            <>
              <p className="text-sm text-[#6f6a5c]">
                {t('verifyEmail.successHelp') ||
                  'Dein Account ist jetzt aktiv. Du kannst direkt loslegen.'}
              </p>
              <Button
                onClick={() => navigate(isAuthenticated() ? '/dashboard' : '/auth')}
                className="w-full bg-[#1f4d3a] hover:bg-[#1f4d3a]"
              >
                {isAuthenticated()
                  ? t('verifyEmail.goToDashboard') || 'Zum Dashboard'
                  : t('verifyEmail.goToLogin') || 'Zum Login'}
              </Button>
            </>
          )}
          {status === 'error' && (
            <>
              <p className="text-sm text-[#a34430]">{errorMessage}</p>
              <p className="text-xs text-[#6f6a5c]">
                {t('verifyEmail.errorHelp') ||
                  'Logge dich ein und klicke im Dashboard auf "Bestätigung erneut senden", um einen neuen Link zu bekommen.'}
              </p>
              <Button
                onClick={() => navigate('/auth')}
                variant="outline"
                className="w-full border-[#d8d0bd]"
              >
                <Mail className="w-4 h-4 mr-2" />
                {t('verifyEmail.goToLogin') || 'Zum Login'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
